/**
 * Coverage region settings — v9.2 / V92-6-2 (D-92-3).
 *
 * Lets an owner/admin choose which regions the workspace operates in.
 * Legislation whose region is outside the covered set is filtered out of
 * applicability (computeApplicability `coverage_regions`). Empty selection
 * = all regions (no filter).
 *
 * NOTE: not yet browser- or e2e-verified (V92-6-5 ux-adversary review +
 * V92-6-6 Playwright are pending per docs/v9.2/remaining-work.md).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, PageHeader, Spinner } from '@aigovops/design-system';
import { useOptionalWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import { getCoverageRegions, setCoverageRegions, getCoverageImpact, type CoverageRegion } from '../lib/api';
import { REGIONS, RegionCheckboxList } from '../components/RegionCheckboxList';

export function CoverageRegionSettings() {
  const workspaceCtx = useOptionalWorkspace();
  const workspaceSlug = workspaceCtx?.workspace.slug ?? null;
  const canManage = workspaceCtx?.role === 'owner' || workspaceCtx?.role === 'admin';

  const regionsAsync = useWorkspaceQuery(
    (s) => queryKeys.coverageRegions(s),
    (s) => getCoverageRegions(s),
  );
  const impactAsync = useWorkspaceQuery(
    (s) => queryKeys.coverageImpact(s),
    (s) => getCoverageImpact(s),
  );

  const [selected, setSelected] = useState<Set<CoverageRegion>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (regionsAsync.data) setSelected(new Set(regionsAsync.data.coverage_regions));
  }, [regionsAsync.data]);

  const dirty = useMemo(() => {
    const server = new Set(regionsAsync.data?.coverage_regions ?? []);
    if (server.size !== selected.size) return true;
    for (const r of selected) if (!server.has(r)) return true;
    return false;
  }, [regionsAsync.data, selected]);

  function toggle(code: CoverageRegion) {
    setSaved(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  async function save() {
    if (!workspaceSlug) return;
    // Error-prevention (ux-flow Blocker): saving a change that REMOVES
    // regions narrows the register — legislation in the dropped regions
    // stops appearing. Name the removed regions and confirm before saving.
    const server = new Set(regionsAsync.data?.coverage_regions ?? []);
    const wasFiltered = server.size > 0; // [] = all regions
    const nowFiltered = selected.size > 0;
    const removed = [...server].filter((r) => !selected.has(r as CoverageRegion));
    const narrowing =
      (wasFiltered && removed.length > 0) || (!wasFiltered && nowFiltered);
    if (narrowing) {
      const detail = !wasFiltered
        ? 'You are switching from all regions to only the selected ones. Legislation outside your selection will no longer appear in this workspace.'
        : `You are removing ${removed.join(', ')}. Legislation in ${
            removed.length > 1 ? 'those regions' : 'that region'
          } will no longer appear in this workspace.`;
      if (!window.confirm(`${detail}\n\nContinue?`)) return;
    }
    setSaving(true);
    setError(null);
    try {
      // Preserve the canonical order from REGIONS.
      const ordered = REGIONS.map((r) => r.code).filter((c) => selected.has(c));
      await setCoverageRegions(workspaceSlug, ordered);
      await regionsAsync.refetch();
      await impactAsync.refetch();
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save coverage regions');
    } finally {
      setSaving(false);
    }
  }

  if (!workspaceSlug) {
    return (
      <Alert variant="error">
        Coverage regions are workspace-scoped. Open this page from within a workspace.
      </Alert>
    );
  }
  if (regionsAsync.loading) return <Spinner label="Loading coverage regions" />;
  if (regionsAsync.error) return <Alert variant="error">{regionsAsync.error}</Alert>;

  const allRegions = selected.size === 0;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)', maxWidth: '42rem' }}>
      <PageHeader
        title="Coverage regions"
        subtitle="Choose the regions this workspace operates in. Legislation outside your regions is filtered from applicability. Select none to cover all regions."
      />

      {/* aria-live status region: save result + errors are announced (a11y). */}
      <div role="status" aria-live="polite">
        {error ? <Alert variant="error">{error}</Alert> : null}
        {saved && !dirty ? <Alert variant="success">Coverage regions saved.</Alert> : null}
      </div>

      {/* Always-visible filter semantics — the consequence must not hide in
          prose (ux-flow/aesthetic High). Both states are stated explicitly. */}
      <Alert variant="info">
        {allRegions
          ? 'No regions selected — all regions are covered (no filter). Legislation from every region appears in this workspace.'
          : 'Only the selected regions apply. Legislation from any unselected region is hidden from this workspace.'}
      </Alert>

      {/* Consequence preview (V92-6-3): the impact reflects the SAVED
          selection, so the user can trust what the register currently shows.
          Addresses the ux-flow Blocker (silent coverage narrowing). */}
      {impactAsync.data ? (
        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)' }}>
          <strong style={{ color: 'var(--text)' }}>{impactAsync.data.applicable_rules}</strong> of{' '}
          {impactAsync.data.total_active_rules} active legislation rules apply in your regions
          {impactAsync.data.filtered_rules > 0 ? (
            <>
              {' '}·{' '}
              <strong style={{ color: 'var(--text)' }}>{impactAsync.data.filtered_rules}</strong> filtered out
              {impactAsync.data.filtered_regions.length > 0
                ? ` (${impactAsync.data.filtered_regions.map((f) => `${f.region} ${f.count}`).join(', ')})`
                : ''}
            </>
          ) : null}
          .
        </p>
      ) : null}

      <RegionCheckboxList
        selected={selected}
        onToggle={toggle}
        disabled={!canManage || saving}
      />

      {canManage ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="primary" onClick={save} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save coverage regions'}
          </Button>
        </div>
      ) : (
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)', margin: 0 }}>
          Coverage regions are managed by workspace owners and admins.{' '}
          <Link to="../team" style={{ color: 'var(--accent)' }}>
            View the team
          </Link>{' '}
          to find one.
        </p>
      )}
    </div>
  );
}
